import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, MapPin, Ticket, ChevronRight, Sun, AlertCircle, CalendarClock, MessageSquare, Users, CheckCircle2, Cake, Heart, StickyNote, Sparkles, CalendarDays } from "lucide-react";
import { SeasonInsights } from "@/components/SeasonInsights";
import { ParkHoursCard } from "@/components/ParkHoursCard";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CallOutButton } from "@/components/CallOut";
import { ScheduleReviewDialog } from "@/components/ScheduleReviewDialog";
import { ShiftNoteDialog } from "@/components/ShiftNoteDialog";
import { tourTitle, statusColor, timeToMinutes, spansToday, etTodayMidnight, dayScheduleFor, hoursBetween } from "@/lib/fields";

const ACCENT = { today: "#22c55e", needs: "#f59e0b", shift: "#22d3ee" };

// One consistent, compact, glanceable card used for every home tour/shift row.
const TourRow = ({ variant, title, titleTone = "text-zinc-100", pill, meta, extras, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group flex w-full items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.04]"
    style={{ borderLeft: `3px solid ${ACCENT[variant]}` }}
  >
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`truncate font-700 ${titleTone}`}>{title}</span>
        {pill}
      </div>
      {meta && <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-400">{meta}</div>}
      {extras}
    </div>
    <ChevronRight className="h-4 w-4 shrink-0 text-zinc-600 group-hover:text-zinc-300" />
  </button>
);

const MetaItem = ({ icon: Icon, tone = "text-zinc-500", children }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5"><Icon className={`h-3.5 w-3.5 shrink-0 ${tone}`} /> <span className="truncate">{children}</span></span>
);

const NeedsCard = ({ tour, onClick, testid }) => (
  <TourRow
    variant="needs"
    title={tourTitle(tour)}
    pill={tour.booking_status ? <span className={`rounded-full px-2 py-0.5 text-[10px] ${statusColor(tour.booking_status)}`}>{tour.booking_status}</span> : null}
    meta={<>
      {tour.meeting_time && <MetaItem icon={Clock}>{tour.meeting_time}</MetaItem>}
      {tour.resort_meet && <MetaItem icon={MapPin}>{tour.resort_meet}</MetaItem>}
      {tour.party_size && <MetaItem icon={Ticket}>party {tour.party_size}</MetaItem>}
    </>}
    onClick={onClick}
    testid={testid}
  />
);

const smsHref = (v) => `sms:${String(v || "").replace(/[^\d+]/g, "")}`;

const TextPill = ({ name, phone, who, testidBase }) => {
  if (!phone) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testidBase}>
      <span className="mr-1 text-[11px] font-600 uppercase tracking-wide text-zinc-500">{who}{name ? ` · ${name}` : ""}</span>
      <a href={smsHref(phone)} onClick={(e) => e.stopPropagation()} data-testid={`${testidBase}-text`}
         className="inline-flex items-center gap-1 rounded-full border border-[#9B2226]/50 bg-[#9B2226]/10 px-3 py-1 text-xs font-600 text-[#E0575B] transition-colors hover:bg-[#9B2226]/20">
        <MessageSquare className="h-3.5 w-3.5" /> Text
      </a>
    </div>
  );
};

// Today's tour uses the same compact frame as every other card, with a green accent + Text/cake affordances.
const TodayCard = ({ tour, contact, onOpen, testid }) => {
  const guestPhone = contact?.guestPhone;
  const pocPhone = contact?.pocPhone || tour.poc_phone;
  const isGifted = !!(tour.gifted_to || pocPhone);
  const done = tour.booking_status === "Complete";
  const celebs = contact?.celebrations || [];
  // For multi-day tours, show the pickup time + meet location for TODAY's day, not Day 1.
  const et = etTodayMidnight();
  const todayStr = `${et.getMonth() + 1}/${et.getDate()}/${et.getFullYear()}`;
  const sched = dayScheduleFor(tour, todayStr);
  const meetTime = sched.meet_time || tour.meeting_time;
  const meetLoc = sched.meet_location || tour.resort_meet;
  const pill = done ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-700 uppercase tracking-wide text-emerald-300" data-testid={`today-completed-badge-${tour.id}`}>
      <CheckCircle2 className="h-3 w-3" /> Completed
    </span>
  ) : (
    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-700 uppercase tracking-wide text-emerald-300">Today</span>
  );
  const meta = (<>
    {meetTime && <MetaItem icon={Clock} tone="text-emerald-400">{meetTime}</MetaItem>}
    {tour.party_size && <MetaItem icon={Users} tone="text-emerald-400">Party {tour.party_size}</MetaItem>}
    {meetLoc && <MetaItem icon={MapPin} tone="text-emerald-400">{meetLoc}</MetaItem>}
  </>);
  const hasExtras = celebs.length > 0 || guestPhone || (isGifted && pocPhone);
  const extras = hasExtras ? (
    <div className="mt-2.5 space-y-2">
      {celebs.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`today-celebration-${tour.id}`}>
          {celebs.map((c, i) => {
            const anni = c.type === "anniversary";
            return (
              <span key={i} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-700 ${anni ? "border-pink-500/40 bg-pink-500/15 text-pink-300" : "border-amber-500/40 bg-amber-500/15 text-amber-300"}`}>
                {anni ? <Heart className="h-3 w-3 text-pink-400" /> : <Cake className="h-3 w-3 text-amber-400" />}
                {c.name ? `${c.name}'s ` : ""}{anni ? "anniversary" : "birthday"} today {anni ? "🥂" : "🎂"}
              </span>
            );
          })}
        </div>
      )}
      {isGifted
        ? <TextPill name={tour.gifted_to} phone={pocPhone} who="POC" testidBase={`today-contact-poc-${tour.id}`} />
        : <TextPill name={null} phone={guestPhone} who="Guest" testidBase={`today-contact-guest-${tour.id}`} />}
    </div>
  ) : null;
  return (
    <TourRow
      variant="today"
      title={tourTitle(tour)}
      titleTone={done ? "text-zinc-400" : "text-zinc-100"}
      pill={pill}
      meta={meta}
      extras={extras}
      onClick={onOpen}
      testid={testid}
    />
  );
};

const parseDateStr = (s) => {
  const m = String(s || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  const y = m[3].length === 2 ? "20" + m[3] : m[3];
  const d = new Date(+y, +m[1] - 1, +m[2]); d.setHours(0, 0, 0, 0);
  return isNaN(d.getTime()) ? null : d;
};

const shiftKindBadge = (s) => {
  if (s.kind === "tour") return { label: "Scheduled Tour", cls: "bg-[#9B2226]/15 text-[#E0575B]" };
  if (s.kind === "standby" || /STANDBY/i.test(s.label || "")) return { label: "Standby", cls: "bg-amber-500/15 text-amber-400" };
  return { label: "Shift", cls: "bg-zinc-500/15 text-zinc-300" };
};
const cleanShiftLabel = (s) => (s.kind === "tour" ? (s.label || "Tour").replace(/\s*\(.*\)$/, "") : (s.label || "Shift"));

const ShiftCard = ({ shift, onClick, testid = "home-next-shift" }) => {
  const d = parseDateStr(shift.date);
  const badge = shiftKindBadge(shift);
  return (
    <TourRow
      variant="shift"
      title={cleanShiftLabel(shift)}
      pill={<span className={`rounded-full px-2 py-0.5 text-[10px] ${badge.cls}`}>{badge.label}</span>}
      meta={<>
        {d && <MetaItem icon={CalendarClock}>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</MetaItem>}
        {shift.start_time && <MetaItem icon={Clock}>{shift.start_time}{shift.end_time ? `–${shift.end_time}` : ""}</MetaItem>}
      </>}
      onClick={onClick}
      testid={testid}
    />
  );
};

// Note pill + preview for a home shift row (shifts only, opens the shared note popup).
const NotePill = ({ s, onOpen }) => (
  <button onClick={(e) => { e.stopPropagation(); onOpen(s); }} data-testid={`home-note-${s.id}`}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-600 transition-colors ${s.note ? "border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:bg-zinc-700/50"}`}>
    <StickyNote className="h-3.5 w-3.5" /> {s.note ? "Note" : "Add note"}
  </button>
);

const NotePreview = ({ s }) => (s.note ? (
  <p className="note-preview flex items-start gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-200/90" data-testid={`home-note-preview-${s.id}`}>
    <StickyNote className="mt-0.5 h-3 w-3 shrink-0" /> <span className="whitespace-pre-wrap break-words">{s.note}</span>
  </p>
) : null);

const Section = ({ icon: Icon, title, accent, right, children }) => (
  <div>
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className={`flex items-center gap-2 font-display text-sm font-700 uppercase tracking-wider ${accent}`}>
        <Icon className="h-4 w-4" /> {title}
      </h2>
      {right}
    </div>
    {children}
  </div>
);

export const HomeView = ({ tours, loaded = true, onUpload, onOpenCalendarDate, onRefresh }) => {
  const etToday = etTodayMidnight();
  const todayMdy = `${etToday.getMonth() + 1}/${etToday.getDate()}/${etToday.getFullYear()}`;
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "";
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const [shifts, setShifts] = useState([]);
  const [noteShift, setNoteShift] = useState(null);
  // Background imports (iPhone Shortcut / SMS) that landed while the guide was away.
  const [imports, setImports] = useState(null);
  const [schedReview, setSchedReview] = useState(false);
  const importSigRef = useRef("");
  const loadImports = useCallback(() => {
    api.getImportEvents()
      .then((r) => {
        if (r && r.total > 0) {
          const sig = JSON.stringify([r.tours.map((t) => t.id), r.shifts_added, r.shifts_changed, r.shifts_removed]);
          if (sig !== importSigRef.current) {
            importSigRef.current = sig;
            setImports(r);
            onRefresh?.(); // pull the freshly-imported tours into the lists (once per change)
          }
        } else {
          importSigRef.current = "";
          setImports(null);
        }
      })
      .catch(() => {});
  }, [onRefresh]);
  useEffect(() => { loadImports(); }, [loadImports]);
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") loadImports(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [loadImports]);
  const dismissImports = async () => {
    const prev = imports;
    setImports(null);
    importSigRef.current = "";
    try {
      await api.dismissImportEvents();
    } catch {
      setImports(prev); // keep it visible so the guide can retry
      toast.error("Couldn't dismiss — please try again");
    }
  };
  // Tapping a tour to review it dismisses ONLY that tour's pill — the schedule pill and any
  // other tour pills stay until the guide reviews them or taps "Got it".
  const dropTourLocal = (r, id) => {
    if (!r) return r;
    const tours = r.tours.filter((t) => t.id !== id);
    return { ...r, tours, total: tours.length + (r.schedule_imports || 0) };
  };
  const dropScheduleLocal = (r) => {
    if (!r) return r;
    return { ...r, shifts_added: 0, shifts_changed: 0, shifts_removed: 0, schedule_imports: 0,
             changes: { ...(r.changes || {}), ambiguous: [] }, total: r.tours.length };
  };
  const openImportedTour = (id) => {
    importSigRef.current = "";
    setImports((r) => dropTourLocal(r, id));
    api.dismissImportEvents({ tour_id: id }).catch(() => {});
    navigate(`/guest/${id}`);
  };
  // Tapping the calendar-update line opens the Calendar tab and clears ONLY the schedule pill.
  const openImportedCalendar = () => {
    importSigRef.current = "";
    setImports((r) => dropScheduleLocal(r));
    api.dismissImportEvents({ kind: "schedule" }).catch(() => {});
    onOpenCalendarDate?.(todayMdy);
  };
  const [schedBusy, setSchedBusy] = useState(false);
  const resolveAmbiguous = async (picks, delIds) => {
    const amb = imports?.changes?.ambiguous || [];
    const shifts = amb.filter((a) => picks[a.date]).map((a) => ({ date: a.date, start_time: a.start, end_time: a.end, label: a.label }));
    setSchedBusy(true);
    try {
      await api.applySchedule(shifts, picks, delIds);
      toast.success("Saved your choice");
      setSchedReview(false);
      importSigRef.current = "";
      setImports((r) => dropScheduleLocal(r));
      api.dismissImportEvents({ kind: "schedule" }).catch(() => {});
      loadShifts();
      onRefresh?.();
    } catch { toast.error("Couldn't save that choice"); }
    finally { setSchedBusy(false); }
  };
  const undoSchedImport = async () => {
    try {
      await api.undoSchedule();
      toast.success("Schedule import undone");
      setSchedReview(false);
      importSigRef.current = "";
      setImports((r) => dropScheduleLocal(r));
      api.dismissImportEvents({ kind: "schedule" }).catch(() => {});
      loadShifts();
      onRefresh?.();
    } catch { toast.error("Couldn't undo the import"); }
  };
  // Once the review dialog is closed (schedule changes have been seen), clear ONLY the
  // schedule pill so tour pills you haven't opened yet remain.
  const closeSchedReview = () => {
    setSchedReview(false);
    if (imports) {
      importSigRef.current = "";
      setImports((r) => dropScheduleLocal(r));
      api.dismissImportEvents({ kind: "schedule" }).catch(() => {});
    }
  };
  const loadShifts = useCallback(() => { api.listShifts().then((s) => setShifts(Array.isArray(s) ? s : [])).catch(() => setShifts([])); }, []);
  useEffect(() => { loadShifts(); }, [loadShifts]);
  useEffect(() => {
    const h = () => { loadShifts(); onRefresh?.(); };
    window.addEventListener("schedule-invalidate", h);
    return () => window.removeEventListener("schedule-invalidate", h);
  }, [loadShifts, onRefresh]);

  const todays = tours.filter((t) => spansToday(t) && ["Current", "Complete"].includes(t.booking_status));
  const needsUpdate = tours.filter((t) => t.booking_status === "Past (Update)");

  // Load contact phones for today's tours so we can tap-to-text at the podium.
  // For gifted tours the number to show is the POC's (stored on the POC's own profile).
  const [todayContacts, setTodayContacts] = useState({});
  const [celebrations, setCelebrations] = useState([]);
  useEffect(() => {
    api.upcomingCelebrations().then((r) => setCelebrations(r.celebrations || [])).catch(() => {});
  }, []);
  const todayKey = todays.map((t) => t.id).join(",");
  useEffect(() => {
    const et = etTodayMidnight();
    const em = et.getMonth() + 1, ed = et.getDate();
    todays.forEach(async (t) => {
      const entry = {};
      let celebs = [];
      if (t.guest_name) {
        try {
          const p = await api.getProfile(t.guest_name);
          entry.guestPhone = p?.phone;
          celebs = p?.celebrations || [];
        } catch { /* ignore */ }
      }
      if (t.gifted_to) {
        try { entry.pocPhone = (await api.getProfile(t.gifted_to))?.phone; } catch { /* ignore */ }
      }
      entry.celebrations = celebs.filter((c) => Number(c.month) === em && Number(c.day) === ed);
      setTodayContacts((m) => ({ ...m, [t.id]: entry }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayKey]);

  // Next upcoming work item — merges scheduled TOURS and calendar SHIFTS, soonest first.
  // (Previously only shifts were considered, so a scheduled tour with no calendar shift got skipped.)
  const tourDates = (t) => (Array.isArray(t.tour_date) ? t.tour_date : [t.tour_date]).map(parseDateStr).filter(Boolean).sort((a, b) => a - b);
  const nextItem = useMemo(() => {
    const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
    const cands = [];
    const tourDateKeys = new Set();
    // Confirmed upcoming tours (exclude possible / complete / past). Today's tour lives above.
    tours.forEach((t) => {
      if (["Complete", "Past (Update)", "Future (Request)", "Future (Possible)"].includes(t.booking_status)) return;
      const dstrs = (Array.isArray(t.tour_date) ? t.tour_date : [t.tour_date]).filter(Boolean);
      const pairs = dstrs.map((s) => ({ s, d: parseDateStr(s) })).filter((p) => p.d && p.d > todayMid).sort((a, b) => a.d - b.d);
      if (pairs.length) {
        const { s, d } = pairs[0];
        const sched = dayScheduleFor(t, s);
        cands.push({ type: "tour", date: d, startMin: timeToMinutes(sched.meet_time) ?? 0, tour: t, sched });
        tourDateKeys.add(d.getTime());
      }
    });
    // Upcoming shifts, but skip tour-kind shifts that duplicate a tour already on that date.
    shifts.forEach((s) => {
      const d = parseDateStr(s.date);
      if (!d || d <= todayMid) return; // today's shifts live in the Today section now
      const isTourShift = s.kind === "tour";
      if (isTourShift && tourDateKeys.has(d.getTime())) return;
      cands.push({ type: "shift", date: d, startMin: timeToMinutes(s.start_time) ?? 9999, shift: s });
    });
    cands.sort((a, b) => (a.date - b.date) || (a.startMin - b.startMin));
    return cands[0] || null;
  }, [tours, shifts]);

  // Shifts happening TODAY belong in the Today section (they're current work), not Next Shift.
  const todayShifts = useMemo(() => {
    const todayTimes = new Set(todays.flatMap((t) => tourDates(t)).map((d) => d.getTime()));
    return shifts.filter((s) => {
      const d = parseDateStr(s.date);
      if (!d || d.getTime() !== today.getTime()) return false;
      if (s.kind === "tour" && todayTimes.has(d.getTime())) return false; // avoid duplicating a today tour
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, todayKey]);

  // "Today" / "Tomorrow" / "in N days" countdown for the Next Shift block.
  const nextCountdown = useMemo(() => {
    if (!nextItem) return null;
    const d = new Date(nextItem.date); d.setHours(0, 0, 0, 0);
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const days = Math.round((d - t) / 86400000);
    if (days <= 0) return "Today";
    if (days === 1) return "Tomorrow";
    return `in ${days} days`;
  }, [nextItem]);

  return (
    <div className="space-y-6 xl:columns-2 xl:gap-x-6 xl:space-y-0 xl:[&>*]:mb-6 xl:[&>*]:break-inside-avoid">
      <div
        className="relative overflow-hidden rounded-2xl border border-zinc-700 shadow-xl shadow-black/30 xl:[column-span:all]"
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(/logo.jpg)" }}
        />
        {/* theme-adaptive veil so plaid stays vivid but text is legible in both modes */}
        <div className="absolute inset-0 hero-veil" />
        <div className="relative flex flex-col items-start gap-2.5 p-3.5 sm:p-4">
          <div className="w-full min-w-0">
            <h1 className="font-display text-xl font-800 leading-tight tracking-tight text-zinc-50 sm:text-2xl" data-testid="home-welcome-heading">{firstName ? `Welcome back, ${firstName}` : "Welcome back"}</h1>
            <p className="text-xs text-zinc-300">Your tours at a glance</p>
          </div>
        </div>
      </div>

      <div className="xl:[column-span:all]">
        <ParkHoursCard />
      </div>

      {imports && imports.total > 0 && (() => {
        const shiftParts = [];
        if (imports.shifts_added > 0) shiftParts.push(`${imports.shifts_added} added`);
        if (imports.shifts_changed > 0) shiftParts.push(`${imports.shifts_changed} changed`);
        if (imports.shifts_removed > 0) shiftParts.push(`${imports.shifts_removed} removed`);
        const ambCount = imports.changes?.ambiguous?.length || 0;
        return (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4" data-testid="home-import-banner">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"><Sparkles className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-sm font-700 tracking-tight text-zinc-50">While you were away</h3>
                {imports.tours.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-600 text-zinc-300" data-testid="import-tours-count">{imports.tours.length} tour{imports.tours.length > 1 ? "s" : ""} added</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {imports.tours.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => openImportedTour(t.id)}
                          className="inline-flex max-w-full items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-600 text-emerald-300 transition-colors hover:bg-emerald-500/20"
                          data-testid={`import-tour-${t.id}`}
                        >
                          <Ticket className="h-3 w-3 shrink-0" /> <span className="truncate">{t.title}</span>
                          {t.needs_review && (
                            <span className="ml-0.5 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-700 uppercase tracking-wide text-amber-300" aria-label="needs review" data-testid={`import-needs-review-${t.id}`}>
                              <AlertCircle className="h-2.5 w-2.5" /> Review
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {(shiftParts.length > 0 || ambCount > 0) && (
                  <button onClick={() => setSchedReview(true)} className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-600 transition-colors ${ambCount > 0 ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"}`} data-testid="import-shifts-summary">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" /> {ambCount > 0 ? `Review calendar · ${ambCount} to confirm` : `Review calendar · ${shiftParts.join(" · ")}`}
                  </button>
                )}
                {imports.tours.length === 0 && shiftParts.length === 0 && ambCount === 0 && (
                  <p className="mt-2 text-xs text-zinc-300" data-testid="import-no-changes">
                    {imports.schedule_imports > 0
                      ? `Schedule imported${imports.shifts_unchanged > 0 ? ` · ${imports.shifts_unchanged} shift${imports.shifts_unchanged === 1 ? "" : "s"}, already up to date` : " · already up to date"}`
                      : "Import complete · nothing new to review"}
                  </p>
                )}
              </div>
              <button onClick={dismissImports} className="shrink-0 rounded-full px-3 py-1.5 text-xs font-600 text-zinc-200 transition-colors hover:bg-white/10" data-testid="import-banner-dismiss">Got it</button>
            </div>
          </div>
        );
      })()}

      {loaded && tours.length === 0 && (
        <div className="rounded-2xl border border-dashed border-[#9B2226]/40 bg-[#9B2226]/[0.06] p-6 text-center" data-testid="home-empty-welcome">
          <h2 className="font-display text-xl font-800 tracking-tight text-zinc-50">Let's build your tour book</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-zinc-300">Tap the <b className="text-zinc-100">+</b> button and choose <b className="text-zinc-100">Import screenshot</b> — Stage Door fills in the details for you — or pick <b className="text-zinc-100">Add tour</b> to enter one by hand. Your guests, schedule and stats all grow from here.</p>
        </div>
      )}

      <Section icon={Sun} title="Today's Tour" accent="text-emerald-400">
        {todays.length === 0 && todayShifts.length === 0 ? (
          <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400" data-testid="home-today-empty">No tours today.</p>
        ) : (
          <div className="space-y-2" data-testid="home-today-list">
            {[...todays].sort((a, b) => (a.booking_status === "Complete" ? 1 : 0) - (b.booking_status === "Complete" ? 1 : 0)).map((t) => (
              <div key={t.id} className="space-y-1">
                <TodayCard tour={t} contact={todayContacts[t.id]} onOpen={() => navigate(`/guest/${t.id}`)} testid={`home-today-${t.id}`} />
                <div className="flex justify-end">
                  <CallOutButton source={{ kind: "tour", id: t.id, label: tourTitle(t), date: todayMdy, multiDay: Array.isArray(t.tour_date) && t.tour_date.length > 1, hours: hoursBetween(dayScheduleFor(t, todayMdy).start, dayScheduleFor(t, todayMdy).end) }} onSaved={onRefresh} testid={`home-callout-tour-${t.id}`} />
                </div>
              </div>
            ))}
            {todayShifts.map((s) => (
              <div key={s.id} className="space-y-1">
                <ShiftCard shift={s} onClick={() => onOpenCalendarDate?.(s.date)} testid={`home-today-shift-${s.id}`} />
                <NotePreview s={s} />
                <div className="flex items-center justify-end gap-2">
                  <NotePill s={s} onOpen={setNoteShift} />
                  <CallOutButton source={{ kind: "shift", id: s.id, label: cleanShiftLabel(s) || "Shift", date: s.date, hours: hoursBetween(s.start_time, s.end_time) }} onSaved={onRefresh} testid={`home-callout-shift-${s.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {celebrations.length > 0 && (
        <Section icon={Cake} title="Upcoming Celebrations" accent="text-amber-400">
          <div className="space-y-2" data-testid="home-celebrations">
            {celebrations.map((c, i) => {
              const soon = c.days_until <= 3;
              return (
                <button
                  key={i}
                  onClick={() => navigate(`/profile/${encodeURIComponent(c.profile)}`)}
                  data-testid={`home-celebration-${i}`}
                  className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${soon ? "border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15" : "border-zinc-800 bg-zinc-900/40 hover:bg-white/5"}`}
                >
                  {c.type === "anniversary" ? <Heart className="h-5 w-5 shrink-0 text-pink-400" /> : <Cake className="h-5 w-5 shrink-0 text-amber-400" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-600 text-zinc-100">
                      {c.name}'s {c.type === "anniversary" ? "anniversary" : "birthday"}
                      {c.turning != null ? <span className="text-zinc-400"> · turning {c.turning}</span> : null}
                    </p>
                    <p className="truncate text-xs text-zinc-400">{c.profile}{c.note ? ` · ${c.note}` : ""}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-700 ${soon ? "bg-amber-500/20 text-amber-300" : "bg-zinc-800 text-zinc-300"}`}>
                    {c.days_until === 0 ? "Today! 🎉" : c.days_until === 1 ? "Tomorrow" : `in ${c.days_until} days`}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      <Section icon={AlertCircle} title="Needs Update (past, not complete)" accent="text-amber-400">
        {needsUpdate.length === 0 ? (
          <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400" data-testid="home-needs-empty">You're all caught up.</p>
        ) : (
          <div className="space-y-2" data-testid="home-needs-list">
            {needsUpdate.map((t) => <NeedsCard key={t.id} tour={t} onClick={() => navigate(`/guest/${t.id}`)} testid={`home-needs-${t.id}`} />)}
          </div>
        )}
      </Section>

      <Section icon={CalendarClock} title="Next Shift" accent="text-zinc-400" right={nextItem && (
        <span className="shrink-0 rounded-full bg-zinc-500/15 px-2.5 py-0.5 text-xs font-700 text-zinc-300" data-testid="next-shift-countdown">{nextCountdown}</span>
      )}>
        {!nextItem ? (
          <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400" data-testid="home-next-empty">Nothing upcoming scheduled.</p>
        ) : nextItem.type === "tour" ? (
          <div data-testid="home-next">
            <TourRow
              variant="shift"
              title={tourTitle(nextItem.tour)}
              pill={<span className="rounded-full bg-[#9B2226]/15 px-2 py-0.5 text-[10px] text-[#E0575B]">Scheduled Tour</span>}
              meta={<>
                <MetaItem icon={CalendarClock}>{nextItem.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</MetaItem>
                {nextItem.sched?.meet_time && <MetaItem icon={Clock}>{nextItem.sched.meet_time}</MetaItem>}
                {nextItem.sched?.meet_location && <MetaItem icon={MapPin}>{nextItem.sched.meet_location}</MetaItem>}
              </>}
              onClick={() => navigate(`/guest/${nextItem.tour.id}`)}
              testid="home-next-tour"
            />
          </div>
        ) : (
          <div data-testid="home-next" className="space-y-1">
            <ShiftCard shift={nextItem.shift} onClick={() => onOpenCalendarDate?.(nextItem.shift.date)} />
            <NotePreview s={nextItem.shift} />
            <div className="flex justify-end">
              <NotePill s={nextItem.shift} onOpen={setNoteShift} />
            </div>
          </div>
        )}
      </Section>

      <ShiftNoteDialog shift={noteShift} onOpenChange={(o) => !o && setNoteShift(null)} onSaved={loadShifts} />
      <ScheduleReviewDialog
        open={schedReview}
        onOpenChange={(o) => (o ? setSchedReview(true) : closeSchedReview())}
        changes={imports?.changes}
        mode="review"
        busy={schedBusy}
        onApply={resolveAmbiguous}
        onUndo={undoSchedImport}
      />
    </div>
  );
};
